import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  MozartDrawer,
  MozartTab,
  MozartRadio,
  MozartSelectField,
} from '@pgforsta/mozart_ui';
import { MozartButton } from '@pgforsta/mozart_ui/lib/v4';
import { useLists } from '../../context/lists';

const DrawerWrapper = styled.div`
  [data-theme='MozartDrawer-Header'] {
    height: auto;
    margin: 0;
    padding: 15px;
    display: flex;
    align-items: center;
  }

  [data-theme='MozartDrawer-HeaderClose'] {
    & > [data-theme='MozartIcon'] {
      padding-top: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  [data-theme='MozartDrawer-Body'] {
    box-sizing: border-box;
    padding: 30px 15px;
  }
`;

const TabWrapper = styled.div`
  [data-theme='MozartTab-Vertical'] {
    display: flex;
  }

  [role='tablist'] {
    width: 30%;
    margin: 0;
    margin-right: 15px;
    flex-shrink: 0;

    li:first-child {
      margin-top: 0;
    }

    li:last-child {
      margin-bottom: 0;
    }
  }

  [data-theme='MozartVerticalTabContent'] {
    width: 70%;
    margin: 0;
    float: none;
    overflow: visible;
  }
`;

const MozartRadioWrapper = styled.div`
  [data-theme='MozartRadio'] {
    margin: 0;

    & > div:first-child {
      margin-top: 0;
    }

    & > div:last-child {
      margin-bottom: 0;
    }
  }
`;

const SelectedFilters = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
  font-weight: 600;
`;

const SelectedFilter = styled.li`
  padding: 15px 0;
  display: flex;
  align-items: center;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #715aff;
  }
`;

const SelectedFilterKey = styled.div`
  flex: 1 1 25%;
  color: #403788;
`;

const SelectedFilterValue = styled.div`
  flex: 1 1 75%;
`;

const Filters = ({ open, selectedFilters, onApply, onClose }) => {
  const { clients } = useLists();
  const [filters, setFilters] = useState({
    entityType: '',
    clientId: '0',
  });

  useEffect(() => {
    if (open) {
      setFilters(selectedFilters);
    }
  }, [open, selectedFilters]);

  const clientsData = useMemo(() => {
    if (!clients.isResolved) {
      return [];
    }
    return [
      { value: '0', text: 'All' },
      ...clients.data.map((client) => ({
        value: client.id,
        text: client.name,
      })),
    ];
  }, [clients.data, clients.isResolved]);

  const selectedClientName = useMemo(
    () =>
      clientsData.find(({ value }) => filters.clientId === value)?.text || '',
    [clientsData, filters.clientId]
  );

  const renderDrawerActions = () => {
    return (
      <MozartButton onClick={() => onApply(filters)}>Apply Filter</MozartButton>
    );
  };

  const renderDrawerContents = () => {
    const { entityType } = filters;
    const tabs = [
      {
        name: 'Current Filter(s) Selected',
        content: (
          <SelectedFilters>
            <SelectedFilter>
              <SelectedFilterKey>Entity Type</SelectedFilterKey>
              <SelectedFilterValue>
                {entityType === '' ? 'All' : entityType}
              </SelectedFilterValue>
            </SelectedFilter>
            <SelectedFilter>
              <SelectedFilterKey>Client</SelectedFilterKey>
              <SelectedFilterValue>{selectedClientName}</SelectedFilterValue>
            </SelectedFilter>
          </SelectedFilters>
        ),
      },
      {
        name: 'Entity Type',
        content: (
          <MozartRadioWrapper>
            <MozartRadio
              content={[
                { name: 'All', value: '', checked: entityType === '' },
                {
                  name: 'Person',
                  value: 'Person',
                  checked: entityType === 'Person',
                },
                {
                  name: 'Location',
                  value: 'Location',
                  checked: entityType === 'Location',
                },
              ]}
              onChange={(data) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  entityType: data.value,
                }));
              }}
            />
          </MozartRadioWrapper>
        ),
      },
      {
        name: 'Client',
        content: (
          <MozartSelectField
            defaultValue={filters.clientId}
            data={clientsData}
            onChange={(value) => {
              setFilters((prevFilters) => ({
                ...prevFilters,
                clientId: value,
              }));
            }}
            searchable
          />
        ),
      },
    ];

    return (
      <TabWrapper>
        <MozartTab
          horizontal={false}
          data={tabs}
          theme="dark"
          themeStyle={{
            backgroundColor: '#FFFFFF',
            boxShadow: '0 9px 24px -20px #8A78FC',
            border: '1px solid #D3CFF8',
          }}
          defaultSelect={0}
        />
      </TabWrapper>
    );
  };

  return (
    <DrawerWrapper>
      <MozartDrawer
        title={renderDrawerActions()}
        open={open}
        handleClose={onClose}
        data={renderDrawerContents()}
      />
    </DrawerWrapper>
  );
};

Filters.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedFilters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Filters;
